// src/pages/CI/GenerationAnalytics.js
//
// Generation & PR analytics for staff (super admin / ci_admin / operations): published figures only, by client, plant and
// time bucket (daily / weekly / monthly). One request to GET /generation/analytics feeds every chart on the page.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  ResponsiveContainer, ComposedChart, Bar, Line, LineChart, AreaChart, Area, ReferenceDot, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ReferenceLine, PieChart, Pie, Cell, BarChart,
} from 'recharts';
import { Zap, Gauge, TrendingUp, TrendingDown, PowerOff, Download, RefreshCw, AlertTriangle, Sun, Building2 } from 'lucide-react';

const API = process.env.REACT_APP_API_BASE_URL;

// the native option list ignores the page theme: without this it opens white-on-white
const SELECT_CSS = `@keyframes gaRise{from{opacity:0;transform:translateY(14px)}to{opacity:1;transform:none}}
@keyframes gaSun{0%,100%{transform:scale(1);opacity:.85}50%{transform:scale(1.1);opacity:1}}
.ga-in>*{animation:gaRise .55s cubic-bezier(.2,.7,.2,1) both}.ga-in>*:nth-child(2){animation-delay:.06s}.ga-in>*:nth-child(3){animation-delay:.12s}.ga-in>*:nth-child(4){animation-delay:.18s}.ga-in>*:nth-child(5){animation-delay:.24s}.ga-in>*:nth-child(n+6){animation-delay:.3s}
.ga-lift{transition:transform .25s ease,box-shadow .25s ease}.ga-lift:hover{transform:translateY(-3px);box-shadow:0 16px 36px -14px rgba(59,130,246,.45)}
.ga-ring{transition:stroke-dashoffset 1.1s cubic-bezier(.2,.7,.2,1)}
.nd-select{color-scheme:dark}.nd-select option{background:#111827;color:#F1F5F9}.nd-select option:checked{background:#2563EB;color:#fff}`;

const T = {
  surface: 'var(--nd-surface)',
  border: 'var(--nd-w08)',
  text: 'var(--nd-text)',
  muted: 'var(--nd-tx35)',
  font: "'DM Sans', sans-serif",
  mono: "'DM Mono', monospace",
};
const PALETTE = ['#3B82F6', '#22D3A5', '#F59E0B', '#A78BFA', '#F472B6', '#38BDF8', '#FB7185', '#84CC16'];
const GOOD = '#22D3A5';
const WARN = '#FBBF24';
const BAD = '#F87171';
const PR_TARGET = 80;

const card = { background: T.surface, border: `1px solid ${T.border}`, borderRadius: 16, padding: 20 };
const input = {
  padding: '9px 12px', borderRadius: 10, border: `1px solid ${T.border}`, background: 'var(--nd-w04)',
  color: T.text, fontFamily: T.font, fontSize: 13, outline: 'none',
};
const pill = (on) => ({
  padding: '7px 13px', borderRadius: 999, border: on ? 'none' : `1px solid ${T.border}`, cursor: 'pointer',
  background: on ? 'linear-gradient(90deg,#3B82F6,#22D3A5)' : 'var(--nd-w04)', color: on ? '#fff' : T.text,
  fontWeight: 700, fontSize: 12, fontFamily: T.font,
});
const ctrlLabel = { fontSize: 10.5, fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: T.muted, marginBottom: 6 };
const sectionTitle = { fontSize: 11, fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase', color: T.muted, marginBottom: 14, paddingLeft: 10, borderLeft: '3px solid #3B82F6' };

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const fmtBucket = (iso, gran) => {
  if (!iso) return '';
  const [y, m, d] = iso.split('-').map(Number);
  if (gran === 'month') return `${MONTHS[m - 1]} ${String(y).slice(2)}`;
  if (gran === 'week') return `${d} ${MONTHS[m - 1]}`;
  return `${d} ${MONTHS[m - 1]}`;
};
const longBucket = (iso, gran) => {
  const [y, m, d] = iso.split('-').map(Number);
  if (gran === 'month') return `${MONTHS[m - 1]} ${y}`;
  if (gran === 'week') return `Week of ${d} ${MONTHS[m - 1]} ${y}`;
  return `${d} ${MONTHS[m - 1]} ${y}`;
};
// always kWh (never MWh / GWh) - staff read these figures against the portals, which are in kWh
const kwh = (v) => (v == null ? '—' : `${Math.round(v).toLocaleString()} kWh`);
const prColor = (v) => (v == null ? T.muted : v >= 85 ? GOOD : v >= PR_TARGET ? '#86EFAC' : v >= 70 ? WARN : BAD);
const iso = (d) => d.toISOString().slice(0, 10);
// yield of the days that have an expected figure / that expected - never total yield / expected
const vsExpected = (p) => (p.expected_kwh > 0 ? ((p.yield_exp_kwh ?? p.yield_kwh) / p.expected_kwh) * 100 : null);
const addDays = (s, n) => iso(new Date(Date.parse(`${s}T00:00:00Z`) + n * 86400000));

// "one bar per ..." that suits the length of the period
const autoGran = (days) => (days <= 31 ? 'day' : days <= 120 ? 'week' : 'month');
const spanOf = (from, to) => Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86400000) + 1;

const PERIODS = [
  ['auto', 'Latest 3 months'],
  ['30', 'Last 30 days'],
  ['7', 'Last 7 days'],
  ['mtd', 'This month'],
  ['last', 'Previous month'],
  ['all', 'All data'],
  ['custom', 'Pick dates…'],
];

// Every period counts back from the newest day that has data (not from today), so "Last 30 days" never lands on an empty stretch.
function presetRange(key, anchor) {
  const end = anchor || iso(new Date());
  const y = Number(end.slice(0, 4));
  const m = Number(end.slice(5, 7));
  const out = (from, to) => ({ from, to, gran: autoGran(spanOf(from, to)) });
  switch (key) {
    case '7': return out(addDays(end, -6), end);
    case '30': return out(addDays(end, -29), end);
    case 'mtd': return out(`${end.slice(0, 7)}-01`, end);
    case 'last': return out(iso(new Date(Date.UTC(y, m - 2, 1))), iso(new Date(Date.UTC(y, m - 1, 0))));
    case 'all': return out(addDays(end, -799), end);
    default: return null;
  }
}

function ChartTip({ active, payload, label, gran, unit }) {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: '#0B1220', border: '1px solid rgba(255,255,255,.12)', borderRadius: 12, padding: '10px 12px', fontFamily: T.font, minWidth: 150, boxShadow: '0 10px 30px rgba(0,0,0,.4)' }}>
      <div style={{ color: '#9AA7BD', fontSize: 11, fontWeight: 700, marginBottom: 6 }}>{longBucket(label, gran)}</div>
      {payload.filter((p) => p.value != null).map((p) => (
        <div key={p.dataKey + p.name} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: '#fff', marginTop: 3 }}>
          <span style={{ width: 8, height: 8, borderRadius: 4, background: p.color || p.fill }} />
          <span style={{ flex: 1, color: '#C8D2E3' }}>{p.name}</span>
          <b style={{ fontFamily: T.mono }}>{unit === '%' ? `${Number(p.value).toFixed(1)}%` : kwh(p.value)}</b>
        </div>
      ))}
    </div>
  );
}

function Delta({ now, before, invert, suffix }) {
  if (now != null && before === 0 && now === 0) return <span style={{ fontSize: 11, color: T.muted }}>same as the previous period (0)</span>;
  if (now == null || before == null || before === 0) return <span style={{ fontSize: 11, color: T.muted }}>no earlier period to compare</span>;
  const diff = suffix === 'pt' ? now - before : ((now - before) / before) * 100;
  const up = diff >= 0;
  const good = invert ? !up : up;
  const Icon = up ? TrendingUp : TrendingDown;
  return (
    <span style={{ fontSize: 11.5, fontWeight: 700, color: good ? GOOD : BAD, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
      <Icon size={13} /> {up ? '+' : ''}{diff.toFixed(1)}{suffix === 'pt' ? ' pt' : '%'} <span style={{ color: T.muted, fontWeight: 500 }}>vs previous period</span>
    </span>
  );
}

function Kpi({ icon, tint, title, value, sub, children }) {
  return (
    <div className="ga-lift" style={{ ...card, padding: 18, position: 'relative', overflow: 'hidden' }}>
      <div style={{ position: 'absolute', right: -24, top: -24, width: 90, height: 90, borderRadius: 45, background: tint, opacity: 0.14 }} />
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: T.muted, fontSize: 11, fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase' }}>
        <span style={{ color: tint, display: 'inline-flex' }}>{icon}</span>{title}
      </div>
      <div style={{ fontSize: 28, fontWeight: 800, letterSpacing: '-0.03em', margin: '8px 0 4px' }}>{value}</div>
      {sub ? <div style={{ fontSize: 12, color: T.muted, marginBottom: 4 }}>{sub}</div> : null}
      {children}
    </div>
  );
}

/** Number that counts up to its value (the hero's big figure). */
function useCountUp(value, ms = 1000) {
  const [shown, setShown] = useState(0);
  useEffect(() => {
    const to = Number(value) || 0;
    let raf;
    const t0 = performance.now();
    const from = 0;
    const step = (t) => {
      const k = Math.min(1, (t - t0) / ms);
      setShown(from + (to - from) * (1 - Math.pow(1 - k, 3)));
      if (k < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [value, ms]);
  return shown;
}

/** Circular gauge 0-100 with a tick at the 80 % target. */
function Ring({ value, size = 120, stroke = 11, color = GOOD, label, sub }) {
  const v = Math.max(0, Math.min(100, Number(value) || 0));
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const ang = (PR_TARGET / 100) * 2 * Math.PI - Math.PI / 2;
  const [on, setOn] = useState(false);
  useEffect(() => { const t = setTimeout(() => setOn(true), 60); return () => clearTimeout(t); }, []);
  return (
    <div style={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--nd-w08)" strokeWidth={stroke} />
        <circle className="ga-ring" cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth={stroke} strokeLinecap="round"
          strokeDasharray={c} strokeDashoffset={on ? c * (1 - v / 100) : c} style={{ filter: `drop-shadow(0 0 6px ${color}88)` }} />
      </svg>
      <svg width={size} height={size} style={{ position: 'absolute', left: 0, top: 0 }}>
        <line x1={size / 2 + (r - stroke) * Math.cos(ang)} y1={size / 2 + (r - stroke) * Math.sin(ang)}
          x2={size / 2 + (r + stroke * 0.7) * Math.cos(ang)} y2={size / 2 + (r + stroke * 0.7) * Math.sin(ang)} stroke="#FBBF24" strokeWidth={2.5} strokeLinecap="round" />
      </svg>
      <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ fontSize: size * 0.22, fontWeight: 800, letterSpacing: '-0.03em', color }}>{label}</div>
        {sub ? <div style={{ fontSize: Math.max(9, size * 0.085), color: T.muted, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' }}>{sub}</div> : null}
      </div>
    </div>
  );
}

/** Tiny area chart under a KPI. */
function Spark({ data, dataKey, color }) {
  if (!data || data.length < 2) return null;
  const id = `sp-${dataKey}-${color.replace('#', '')}`;
  return (
    <div style={{ height: 38, margin: '6px -6px -6px' }}>
      <ResponsiveContainer>
        <AreaChart data={data} margin={{ top: 4, right: 0, left: 0, bottom: 0 }}>
          <defs>
            <linearGradient id={id} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={color} stopOpacity={0.5} />
              <stop offset="100%" stopColor={color} stopOpacity={0} />
            </linearGradient>
          </defs>
          <Area type="monotone" dataKey={dataKey} stroke={color} strokeWidth={2} fill={`url(#${id})`} dot={false} isAnimationActive={false} connectNulls />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

/** The donut: only clients with PUBLISHED data in the selected period (a client with no published report is not listed at all).
 *  One client selected -> its plants instead, so the chart is never one full ring. Every % is of the sum of what is shown. */
function ShareCard({ data, clientId, colorOf }) {
  const byClient = !clientId;
  const slices = (byClient
    ? data.by_client.map((c) => ({ id: c.client_id, name: c.client_name, kwh: c.yield_kwh, color: colorOf[c.client_name] || '#64748B' }))
    : data.by_plant.map((p, i) => ({ id: p.plant_id, name: p.plant_name, kwh: p.yield_kwh, color: PALETTE[i % PALETTE.length] })))
    .filter((x) => x.kwh > 0)
    .sort((a, b) => b.kwh - a.kwh);
  const total = slices.reduce((a, x) => a + x.kwh, 0);
  const title = byClient ? 'Share of generation by client' : `Share of generation by plant — ${data.by_client[0]?.client_name || ''}`;
  return (
    <div style={card}>
      <div style={sectionTitle}>{title}</div>
      <div style={{ minHeight: 280, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <div style={{ width: 230, height: 230, position: 'relative', flexShrink: 0, margin: '0 auto' }}>
          <ResponsiveContainer>
            <PieChart>
              <Pie data={slices} dataKey="kwh" nameKey="name" innerRadius="60%" outerRadius="94%" paddingAngle={slices.length > 1 ? 2 : 0} stroke="none" isAnimationActive={false}>
                {slices.map((x) => <Cell key={x.id} fill={x.color} />)}
              </Pie>
              <Tooltip formatter={(v) => kwh(v)} contentStyle={{ background: '#0B1220', border: '1px solid rgba(255,255,255,.12)', borderRadius: 12, fontFamily: T.font, fontSize: 12 }} itemStyle={{ color: '#fff' }} />
            </PieChart>
          </ResponsiveContainer>
          <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
            <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: T.muted }}>Total</div>
            <div style={{ fontSize: 17, fontWeight: 800, letterSpacing: '-0.02em' }}>{Math.round(total).toLocaleString()}</div>
            <div style={{ fontSize: 10.5, color: T.muted }}>kWh</div>
          </div>
        </div>
        <div style={{ flex: '1 1 220px', maxHeight: 280, overflowY: 'auto' }}>
          {slices.map((x) => (
            <div key={x.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', fontSize: 12.5 }}>
              <span style={{ width: 10, height: 10, borderRadius: 3, background: x.color, flexShrink: 0 }} />
              <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{x.name}</span>
              <span style={{ fontFamily: T.mono, color: T.muted, fontSize: 11.5 }}>{kwh(x.kwh)}</span>
              <b style={{ fontFamily: T.mono, minWidth: 44, textAlign: 'right' }}>{total ? ((x.kwh / total) * 100).toFixed(1) : '0.0'}%</b>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/** The headline: total generation counting up, the PR dial and the key numbers, on a sunrise-coloured backdrop. */
function Hero({ k, range, gran }) {
  const total = useCountUp(k.yield_kwh);
  const pr = k.pr;
  const vs = k.vs_expected_pct;
  return (
    <div style={{
      ...card, padding: 0, position: 'relative', overflow: 'hidden', marginBottom: 18, border: '1px solid rgba(59,130,246,.3)',
      background: 'linear-gradient(120deg, rgba(37,99,235,.30) 0%, rgba(34,211,165,.18) 48%, rgba(251,191,36,.16) 100%), var(--nd-surface)',
    }}>
      {/* sun + rays */}
      <div style={{ position: 'absolute', right: -70, top: -90, width: 320, height: 320, borderRadius: '50%', background: 'radial-gradient(circle, rgba(251,191,36,.55) 0%, rgba(251,191,36,.18) 38%, transparent 68%)', animation: 'gaSun 5s ease-in-out infinite' }} />
      <svg width="100%" height="100%" style={{ position: 'absolute', inset: 0, opacity: 0.07, pointerEvents: 'none' }}>
        <defs><pattern id="gaGrid" width="26" height="26" patternUnits="userSpaceOnUse"><path d="M26 0H0V26" fill="none" stroke="#fff" strokeWidth="1" /></pattern></defs>
        <rect width="100%" height="100%" fill="url(#gaGrid)" />
      </svg>
      <div style={{ position: 'relative', display: 'flex', flexWrap: 'wrap', gap: 28, alignItems: 'center', padding: '26px 28px' }}>
        <div style={{ flex: '1 1 300px', minWidth: 260 }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: 11, fontWeight: 800, letterSpacing: '0.14em', textTransform: 'uppercase', color: '#FCD34D' }}>
            <Sun size={14} /> Energy generated
          </div>
          <div style={{ fontSize: 'clamp(34px, 5vw, 56px)', fontWeight: 900, letterSpacing: '-0.04em', lineHeight: 1.05, margin: '8px 0 6px',
            background: 'linear-gradient(90deg,#fff,#A7F3D0)', WebkitBackgroundClip: 'text', backgroundClip: 'text', color: 'transparent' }}>
            {Math.round(total).toLocaleString()} <span style={{ fontSize: '0.4em', fontWeight: 700, letterSpacing: 0 }}>kWh</span>
          </div>
          <div style={{ fontSize: 13, color: T.muted }}>
            {longBucket(range.from, 'day')} → {longBucket(range.to, 'day')} · {range.days} days · {gran === 'day' ? 'daily' : gran === 'week' ? 'weekly' : 'monthly'} view
          </div>
          <div style={{ marginTop: 10 }}>
            <Delta now={k.yield_kwh} before={k.prev.yield_kwh} />
            {k.prev.yield_kwh != null && (k.prev.days_with_data !== k.days_with_data || k.prev.plants !== k.plants) && (
              <div style={{ marginTop: 6, fontSize: 11.5, color: WARN, fontWeight: 600, maxWidth: 420 }}>
                ⚠ Not like-for-like: this period has data on {k.days_with_data} day{k.days_with_data === 1 ? '' : 's'} from {k.plants} plant{k.plants === 1 ? '' : 's'},
                the previous one on {k.prev.days_with_data} day{k.prev.days_with_data === 1 ? '' : 's'} from {k.prev.plants}.
              </div>
            )}
          </div>
        </div>

        <div style={{ display: 'flex', gap: 22, alignItems: 'center', flexWrap: 'wrap' }}>
          <Ring value={pr} size={132} color={prColor(pr)} label={pr == null ? '—' : `${pr.toFixed(1)}%`} sub="Avg PR" />
          <Ring value={Math.min(vs ?? 0, 100)} size={132} color={vs == null ? T.muted : vs >= 100 ? GOOD : vs >= 85 ? '#38BDF8' : WARN} label={vs == null ? '—' : `${Math.round(vs)}%`} sub="of expected" />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,minmax(96px,1fr))', gap: 10, flex: '0 1 250px' }}>
          {[
            ['Clients', k.clients, '#A78BFA'],
            ['Plants', k.plants, '#38BDF8'],
            ['Offline days', k.offline_days, k.offline_days ? BAD : GOOD],
            ['Best period', k.best_bucket ? fmtBucket(k.best_bucket.bucket, gran) : '—', '#FBBF24'],
          ].map(([l, v, c]) => (
            <div key={l} style={{ background: 'rgba(255,255,255,.06)', border: '1px solid rgba(255,255,255,.1)', borderRadius: 14, padding: '10px 12px', backdropFilter: 'blur(6px)' }}>
              <div style={{ fontSize: 9.5, fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: T.muted }}>{l}</div>
              <div style={{ fontSize: 20, fontWeight: 800, color: c, marginTop: 2 }}>{v}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/** One card per CLIENT (a client with several plants / inverters is still one client): total, PR dial, and its plants listed inside. */
function Spotlight({ clients, plants, colorOf }) {
  const max = clients[0]?.yield_kwh || 1;
  const medal = ['🥇', '🥈', '🥉'];
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={sectionTitle}>Client spotlight</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(310px,1fr))', gap: 14 }}>
        {clients.map((cl, i) => {
          const accent = colorOf[cl.client_name] || '#3B82F6';
          const mine = plants.filter((p) => p.client_id === cl.client_id);
          const vs = cl.expected_kwh > 0 ? ((cl.yield_exp_kwh ?? cl.yield_kwh) / cl.expected_kwh) * 100 : null;
          const first = mine.map((p) => p.first_day).filter(Boolean).sort()[0];
          const last = mine.map((p) => p.last_day).filter(Boolean).sort().slice(-1)[0];
          const kwp = mine.reduce((a, p) => a + (Number(p.dc_capacity_kwp) || 0), 0);
          return (
            <div key={cl.client_id} className="ga-lift" style={{ ...card, padding: 16, position: 'relative', overflow: 'hidden' }}>
              <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 4, background: `linear-gradient(${accent}, transparent)` }} />
              <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                <Ring value={cl.pr} size={84} stroke={9} color={prColor(cl.pr)} label={cl.pr == null ? '—' : `${Math.round(cl.pr)}`} sub="PR" />
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ fontSize: 16, fontWeight: 800, lineHeight: 1.2 }}>
                    {i < 3 && clients.length > 1 ? `${medal[i]} ` : ''}{cl.client_name}
                  </div>
                  <div style={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.02em', marginTop: 4 }}>{kwh(cl.yield_kwh)}</div>
                </div>
              </div>
              <div style={{ height: 6, borderRadius: 3, background: 'var(--nd-w06)', margin: '12px 0 8px' }}>
                <div style={{ height: 6, borderRadius: 3, width: `${Math.max((cl.yield_kwh / max) * 100, 3)}%`, background: `linear-gradient(90deg, ${accent}, #22D3A5)` }} />
              </div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
                <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 8px', borderRadius: 999, background: 'var(--nd-w05)', color: T.muted }}>
                  {mine.length} plant{mine.length === 1 ? '' : 's'}{kwp ? ` · ${kwp.toLocaleString()} kWp` : ''}
                </span>
                {vs != null && <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 8px', borderRadius: 999, background: 'var(--nd-w05)', color: vs >= 100 ? GOOD : vs >= 90 ? T.text : WARN }}>{vs.toFixed(0)}% of expected</span>}
                <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 8px', borderRadius: 999, background: cl.offline_days ? 'rgba(248,113,113,.15)' : 'rgba(34,211,165,.12)', color: cl.offline_days ? BAD : GOOD }}>
                  {cl.offline_days ? `${cl.offline_days} offline day${cl.offline_days > 1 ? 's' : ''}` : 'always online'}
                </span>
                {first && (
                  <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 8px', borderRadius: 999, background: 'var(--nd-w05)', color: T.muted }}>
                    {fmtBucket(first, 'day')}{last !== first ? ` – ${fmtBucket(last, 'day')}` : ''}
                  </span>
                )}
              </div>
              {/* the client's plants, listed inside its card */}
              <div style={{ borderTop: `1px solid ${T.border}`, paddingTop: 10, display: 'flex', flexDirection: 'column', gap: 8 }}>
                {mine.map((p) => (
                  <div key={p.plant_id}>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, fontSize: 12.5 }}>
                      <span style={{ fontWeight: 700, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.plant_name}</span>
                      <span style={{ fontFamily: T.mono, color: T.muted }}>{kwh(p.yield_kwh)}</span>
                      <span style={{ fontWeight: 800, minWidth: 44, textAlign: 'right', color: prColor(p.pr) }}>{p.pr == null ? '—' : `${p.pr.toFixed(1)}%`}</span>
                    </div>
                    <div style={{ height: 4, borderRadius: 2, background: 'var(--nd-w06)', marginTop: 3 }}>
                      <div style={{ height: 4, borderRadius: 2, width: `${Math.max(cl.yield_kwh ? (p.yield_kwh / cl.yield_kwh) * 100 : 0, 2)}%`, background: accent, opacity: 0.85 }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

const INV_COLORS = ['#38BDF8', '#F59E0B', '#A78BFA', '#F472B6', '#84CC16', '#FB7185'];

const hhmm = (m) => `${String(Math.floor(m / 60) % 24).padStart(2, '0')}:${String(Math.round(m % 60)).padStart(2, '0')}`;

/** One inverter's own production curve (read from the picture uploaded with the report), with the peak and every dip marked. */
function InverterCurveCard({ inv, share, color, entries, axisTick }) {
  const [day, setDay] = useState('');
  const [mode, setMode] = useState('picture');
  const [pic, setPic] = useState('');
  const chosen = entries.find((e) => e.date === day) || entries[0];
  const curve = chosen?.curve;
  const picId = chosen?.has_picture ? chosen.id : null;
  // the picture needs the staff token, so it is fetched through axios and shown from a blob URL
  useEffect(() => {
    setPic('');
    if (!picId) return undefined;
    let alive = true;
    let url = '';
    axios.get(`${API}/generation/inverter-picture/${picId}`, { responseType: 'blob' })
      .then((r) => { if (alive) { url = URL.createObjectURL(r.data); setPic(url); } })
      .catch(() => {});
    return () => { alive = false; if (url) URL.revokeObjectURL(url); };
  }, [picId]);
  const view = useMemo(() => {
    if (!curve?.pts?.length) return null;
    const on = curve.pts.filter(([, kw]) => kw > 0.05);
    if (!on.length) return null;
    const from = Math.max(0, Math.floor((on[0][0] - 60) / 60) * 60);
    const to = Math.min(1440, Math.ceil((on[on.length - 1][0] + 60) / 60) * 60);
    const data = curve.pts.filter(([m]) => m >= from && m <= to).map(([m, kw]) => ({ m, kw }));
    const ticks = [];
    for (let t = from; t <= to; t += 120) ticks.push(t);
    return { data, from, to, ticks };
  }, [curve]);

  return (
    <div style={{ ...card, padding: 16, borderTop: `4px solid ${color}` }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 4 }}>
        <div style={{ flex: 1, minWidth: 140 }}>
          <div style={{ fontSize: 15, fontWeight: 800 }}>{inv.key}</div>
          <div style={{ fontSize: 12, color: T.muted }}>{kwh(inv.total)} · {share.toFixed(0)}% of the inverters shown</div>
        </div>
        {entries.length > 1 && (
          <select className="nd-select" style={{ ...input, padding: '6px 10px', fontSize: 12 }} value={chosen.date} onChange={(e) => setDay(e.target.value)}>
            {entries.map((e) => <option key={e.date} value={e.date}>{longBucket(e.date, 'day')}</option>)}
          </select>
        )}
      </div>
      {picId && view && (
        <div style={{ display: 'flex', gap: 6, margin: '8px 0' }}>
          <button style={pill(mode === 'picture')} onClick={() => setMode('picture')}>Report graph</button>
          <button style={pill(mode === 'chart')} onClick={() => setMode('chart')}>Interactive</button>
        </div>
      )}
      {picId && (mode === 'picture' || !view) ? (
        <div style={{ margin: '8px 0 4px' }}>
          {pic ? (
            <img src={pic} alt={`${inv.key} production curve`} style={{ width: '100%', height: 'auto', borderRadius: 12, background: '#fff', display: 'block' }} />
          ) : (
            <div style={{ fontSize: 12.5, color: T.muted, padding: '40px 0', textAlign: 'center' }}>Loading the graph…</div>
          )}
          <div style={{ fontSize: 12, color: T.muted, marginTop: 6 }}>
            {longBucket(chosen.date, 'day')}
            {curve ? (curve.dips.length === 0 ? ' · smooth curve, no dips' : ` · ${curve.dips.length} dip${curve.dips.length > 1 ? 's' : ''} marked (load-related)`) : ''}
          </div>
        </div>
      ) : !view ? (
        <div style={{ fontSize: 12.5, color: T.muted, padding: '26px 0', textAlign: 'center' }}>
          No production-curve picture was uploaded for this inverter, so only its total is shown.
        </div>
      ) : (
        <>
          <div style={{ height: 250 }}>
            <ResponsiveContainer>
              <AreaChart data={view.data} margin={{ top: 28, right: 14, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id={`ic-${inv.key.replace(/\W/g, '')}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={color} stopOpacity={0.45} />
                    <stop offset="100%" stopColor={color} stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="var(--nd-w05)" vertical={false} />
                <XAxis type="number" dataKey="m" domain={[view.from, view.to]} ticks={view.ticks} tickFormatter={hhmm} tick={axisTick} tickLine={false} axisLine={false} />
                <YAxis tick={axisTick} tickLine={false} axisLine={false} width={40} tickFormatter={(v) => `${v}`} unit="" />
                <Tooltip
                  content={({ active, payload }) => (active && payload?.length ? (
                    <div style={{ background: '#0B1220', border: '1px solid rgba(255,255,255,.12)', borderRadius: 10, padding: '8px 10px', fontFamily: T.font, color: '#fff', fontSize: 12 }}>
                      {hhmm(payload[0].payload.m)} · <b>{Number(payload[0].value).toFixed(1)} kW</b>
                    </div>
                  ) : null)}
                />
                <Area type="monotone" dataKey="kw" name="kW" stroke={color} strokeWidth={2.4} fill={`url(#ic-${inv.key.replace(/\W/g, '')})`} dot={false} isAnimationActive={false} />
                <ReferenceDot x={curve.peak.m} y={curve.peak.kw} r={6} fill="#33A9E9" stroke="#fff" strokeWidth={2}
                  label={{ value: `peak ${curve.peak.kw} kW`, position: 'top', fill: 'var(--nd-text)', fontSize: 11, fontWeight: 700 }} />
                {curve.dips.map((d) => (
                  <ReferenceDot key={d.m} x={d.m} y={d.kw} r={6} fill={d.kind === 'drop' ? BAD : '#F59E0B'} stroke="#fff" strokeWidth={2}
                    label={{ value: `${d.kind === 'drop' ? 'sharp drop' : 'small kink'} ~${hhmm(d.m)}`, position: 'bottom', fill: d.kind === 'drop' ? BAD : '#F59E0B', fontSize: 11, fontWeight: 700 }} />
                ))}
              </AreaChart>
            </ResponsiveContainer>
          </div>
          <div style={{ fontSize: 12, color: T.muted, marginTop: 4 }}>
            {longBucket(chosen.date, 'day')} · kW
            {curve.dips.length === 0 ? ' · smooth curve, no dips' : ` · ${curve.dips.length} dip${curve.dips.length > 1 ? 's' : ''} marked (load-related)`}
          </div>
        </>
      )}
    </div>
  );
}

export default function GenerationAnalytics({ clients }) {
  const [clientId, setClientId] = useState('');
  const [plantId, setPlantId] = useState('');
  const [plants, setPlants] = useState([]);
  const [gran, setGran] = useState('week');
  const [preset, setPreset] = useState('auto');
  const [range, setRange] = useState({ from: '', to: '' });
  const [latest, setLatest] = useState('');
  const [layout, setLayout] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    setPlantId('');
    axios
      .get(`${API}/generation/plants`, { params: { client_id: clientId || undefined } })
      .then((r) => setPlants(r.data.plants || []))
      .catch(() => setPlants([]));
  }, [clientId]);

  const load = useCallback(() => {
    let alive = true;
    setLoading(true);
    setError('');
    axios
      .get(`${API}/generation/analytics`, {
        params: {
          granularity: gran,
          client_id: clientId || undefined,
          plant_id: plantId || undefined,
          from: preset === 'auto' ? undefined : range.from || undefined,
          to: preset === 'auto' ? undefined : range.to || undefined,
        },
      })
      .then((r) => {
        if (!alive) return;
        // an older backend (route not uploaded yet) can answer with a web page instead of JSON
        if (!r.data || r.data.success !== true || !('kpis' in r.data)) {
          setData(null);
          setError('The server does not have the analytics route yet - upload routes/generationRoutes.js and restart the backend.');
          return;
        }
        setData(r.data);
        if (preset === 'auto' && r.data.range) {
          setRange({ from: r.data.range.from, to: r.data.range.to });
          setLatest(r.data.range.to);
        }
      })
      .catch((e) => alive && setError(e?.response?.data?.error || 'Could not load the analytics.'))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [gran, clientId, plantId, preset, range.from, range.to]);

  useEffect(() => load(), [load]);

  const pickPreset = (key) => {
    setPreset(key);
    if (key === 'auto') {
      setGran('week'); // the server picks the 90 days ending at the newest data
      return;
    }
    const p = presetRange(key, latest);
    if (p) {
      setRange({ from: p.from, to: p.to });
      setGran(p.gran);
    }
  };
  const setDates = (patch) => {
    const next = { ...range, ...patch };
    setPreset('custom');
    setRange(next);
    if (next.from && next.to && next.from <= next.to) setGran(autoGran(spanOf(next.from, next.to)));
  };

  const clientNames = useMemo(() => (data?.by_client || []).map((c) => c.client_name), [data]);
  const colorOf = useMemo(() => {
    const m = {};
    clientNames.forEach((n, i) => { m[n] = PALETTE[i % PALETTE.length]; });
    return m;
  }, [clientNames]);

  // With several clients every bar / line / legend entry names its client too: "Plant · Client" (same-client plants sit together)
  const plantLabel = useMemo(() => {
    const multi = (data?.by_client || []).length > 1;
    return (p) => (multi ? `${p.plant_name} · ${p.client_name}` : p.plant_name);
  }, [data]);
  const plantNames = useMemo(
    () => [...(data?.by_plant || [])].sort((a, b) => a.client_name.localeCompare(b.client_name) || a.plant_name.localeCompare(b.plant_name)).map(plantLabel),
    [data, plantLabel],
  );
  const plantColor = useMemo(() => {
    const m = {};
    plantNames.forEach((n, i) => { m[n] = PALETTE[i % PALETTE.length]; });
    return m;
  }, [plantNames]);
  // one row per period: <plant> = kWh, "pr|<plant>" = PR, expected = all selected plants together
  const plantRows = useMemo(() => {
    const m = new Map();
    (data?.by_plant || []).forEach((p) => {
      const ps = (data.plant_series || []).find((x) => x.plant_id === p.plant_id);
      (ps?.series || []).forEach((r) => {
        if (!m.has(r.bucket)) m.set(r.bucket, { bucket: r.bucket, expected_kwh: 0 });
        const row = m.get(r.bucket);
        row[plantLabel(p)] = r.yield_kwh;
        row[`pr|${plantLabel(p)}`] = r.pr;
        row.expected_kwh += r.expected_kwh;
      });
    });
    return [...m.values()].sort((a, b) => (a.bucket < b.bucket ? -1 : 1));
  }, [data, plantLabel]);
  // plant exports (PCL / PSM / BSP) carry no expected figure: do not draw a flat "expected 0" line for them
  const hasExpected = (data?.by_plant || []).length > 0 && (data?.by_plant || []).every((p) => p.expected_kwh > 0);
  // a single day (e.g. one Salitex report) is one point: a line needs visible dots to show at all
  const showDots = plantRows.length <= 31;
  const invRows = useMemo(() => {
    const m = new Map();
    (data?.inverters || []).forEach((inv) => {
      const plant = (data.by_plant || []).find((p) => p.plant_id === inv.plant_id);
      const key = `${plant?.plant_name || 'Plant'} · ${inv.name}`;
      inv.series.forEach((r) => {
        if (!m.has(r.bucket)) m.set(r.bucket, { bucket: r.bucket });
        m.get(r.bucket)[key] = r.yield_kwh;
      });
    });
    return [...m.values()].sort((a, b) => (a.bucket < b.bucket ? -1 : 1));
  }, [data]);
  const invKeys = useMemo(
    () => (data?.inverters || []).map((inv) => {
      const plant = (data.by_plant || []).find((p) => p.plant_id === inv.plant_id);
      return { key: `${plant?.plant_name || 'Plant'} · ${inv.name}`, total: inv.yield_kwh, plant_id: inv.plant_id };
    }),
    [data],
  );
  const multiClient = clientNames.length > 1;
  const multiPlant = plantNames.length > 1;
  // few plants read best side by side; many plants stack
  const mode = layout || (plantNames.length <= 3 ? 'side' : 'stack');
  const heat = useMemo(() => {
    const cells = data?.cells || [];
    const buckets = [...new Set(cells.map((c) => c.bucket))].sort();
    const rows = [];
    const seen = new Map();
    cells.forEach((c) => {
      if (!seen.has(c.plant_id)) {
        const row = { plant_id: c.plant_id, plant: c.plant_name, client: c.client_name, by: {} };
        seen.set(c.plant_id, row);
        rows.push(row);
      }
      seen.get(c.plant_id).by[c.bucket] = c;
    });
    return { buckets, rows };
  }, [data]);

  const attention = useMemo(() => {
    const out = [];
    (data?.by_plant || []).forEach((p) => {
      // each plant has its own benchmark (Salitex 80, Power Cement 76, BSP 80, PSM 1MW 80, PSM 2MW 75)
      const target = p.pr_target ?? PR_TARGET;
      if (p.pr != null && p.pr < target) out.push({ tone: 'warn', plant: p, text: `PR ${p.pr.toFixed(1)}% is below its ${target}% target` });
      if (p.offline_days > 0) out.push({ tone: 'bad', plant: p, text: `${p.offline_days} offline day${p.offline_days > 1 ? 's' : ''} in this period` });
    });
    return out;
  }, [data]);

  const exportCsv = () => {
    const rows = [['Client', 'Plant', 'Period', 'Generation kWh', 'PR %', 'Offline days']];
    (data?.cells || []).forEach((c) => rows.push([c.client_name, c.plant_name, c.bucket, c.yield_kwh, c.pr ?? '', c.offline_days]));
    const csv = rows.map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    a.download = `generation_${data.range.from}_${data.range.to}_${gran}.csv`;
    a.click();
  };

  const axisTick = { fill: 'var(--nd-tx35)', fontSize: 11, fontFamily: T.font };
  const k = data?.kpis;
  const noData = !loading && (!data || data.empty);

  return (
    <div>
      <style>{SELECT_CSS}</style>
      {/* ───── controls: WHAT to look at, WHEN, and how finely to split it ───── */}
      <div style={{ ...card, marginBottom: 16, padding: 16 }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 18, alignItems: 'flex-end' }}>
          <div>
            <div style={ctrlLabel}>1 · Which client / plant</div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <select className="nd-select" style={{ ...input, minWidth: 190 }} value={clientId} onChange={(e) => setClientId(e.target.value)}>
                <option value="">All clients</option>
                {clients.map((c) => <option key={c.id} value={c.id}>{c.client_name}</option>)}
              </select>
              <select className="nd-select" style={{ ...input, minWidth: 160 }} value={plantId} onChange={(e) => setPlantId(e.target.value)}>
                <option value="">All plants</option>
                {plants.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
          </div>

          <div>
            <div style={ctrlLabel}>2 · Which period</div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
              <select className="nd-select" style={{ ...input, minWidth: 170, fontWeight: 700 }} value={preset} onChange={(e) => pickPreset(e.target.value)}>
                {PERIODS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
              {preset === 'custom' && (
                <>
                  <input style={input} type="date" value={range.from} max={range.to || undefined} onChange={(e) => setDates({ from: e.target.value })} />
                  <span style={{ color: T.muted }}>to</span>
                  <input style={input} type="date" value={range.to} min={range.from || undefined} onChange={(e) => setDates({ to: e.target.value })} />
                </>
              )}
            </div>
          </div>

          <div>
            <div style={ctrlLabel}>3 · One bar per</div>
            <div style={{ display: 'flex', gap: 6 }}>
              {[['day', 'Day'], ['week', 'Week'], ['month', 'Month']].map(([v, l]) => (
                <button key={v} style={pill(gran === v)} onClick={() => setGran(v)}>{l}</button>
              ))}
            </div>
          </div>

          <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
            <button style={pill(false)} onClick={load} title="Refresh"><RefreshCw size={13} style={{ verticalAlign: -2 }} /></button>
            <button style={pill(false)} onClick={exportCsv} disabled={noData || !data}><Download size={13} style={{ verticalAlign: -2 }} /> CSV</button>
          </div>
        </div>

        {/* what the page is showing right now, in one sentence */}
        {data && data.range && (
          <div style={{ marginTop: 12, paddingTop: 10, borderTop: `1px solid ${T.border}`, fontSize: 12.5, color: T.muted }}>
            Showing <b style={{ color: T.text }}>{clientId ? (clients.find((c) => String(c.id) === String(clientId))?.client_name || 'this client') : 'all clients'}</b>
            {plantId ? <>, <b style={{ color: T.text }}>{plants.find((p) => String(p.id) === String(plantId))?.name || 'one plant'}</b></> : ''}
            {' · '}<b style={{ color: T.text }}>{longBucket(data.range.from, 'day')} – {longBucket(data.range.to, 'day')}</b> ({data.range.days} days)
            {' · one bar per '}<b style={{ color: T.text }}>{gran}</b>
          </div>
        )}
      </div>

      {error ? <div style={{ ...card, color: BAD }}>{error}</div> : null}
      {loading && !data ? <div style={{ ...card, color: T.muted }}>Loading…</div> : null}
      {noData && !error ? (
        <div style={{ ...card, textAlign: 'center', padding: 48, color: T.muted }}>
          <Sun size={34} color="#3B82F6" />
          <div style={{ fontSize: 16, fontWeight: 700, color: T.text, marginTop: 10 }}>No published data for this selection</div>
          <div style={{ fontSize: 13, marginTop: 4 }}>Generate a report and publish it (Reports tab), or widen the dates / choose All clients.</div>
        </div>
      ) : null}

      {data && !data.empty ? (
        <div className="ga-in" style={{ opacity: loading ? 0.55 : 1, transition: 'opacity .2s' }}>
          <Hero k={k} range={data.range} gran={gran} />

          {/* ───── KPIs ───── */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(215px,1fr))', gap: 14, marginBottom: 16 }}>
            <Kpi icon={<Zap size={15} />} tint="#3B82F6" title="Generation" value={kwh(k.yield_kwh)}
              sub={k.vs_expected_pct != null ? `${k.vs_expected_pct}% of expected (${kwh(k.yield_with_expected_kwh)} vs ${kwh(k.expected_kwh)}) · ${k.expected_plants} of ${k.plants} plants have an expected figure` : 'No expected figure in these reports'}>
              <Delta now={k.yield_kwh} before={k.prev.yield_kwh} />
              <Spark data={data.series} dataKey="yield_kwh" color="#3B82F6" />
            </Kpi>
            <Kpi icon={<Gauge size={15} />} tint={prColor(k.pr)} title="Average PR" value={k.pr == null ? '—' : `${k.pr.toFixed(1)}%`}
              sub={k.pr == null ? null : `${k.pr >= PR_TARGET ? 'On target' : 'Below the'} ${PR_TARGET}% target · mean of the daily PR of every online plant-day`}>
              <Delta now={k.pr} before={k.prev.pr} suffix="pt" />
              <Spark data={data.series} dataKey="pr" color={prColor(k.pr)} />
            </Kpi>
            <Kpi icon={<PowerOff size={15} />} tint={k.offline_days ? BAD : GOOD} title="Offline days" value={k.offline_days}
              sub={k.offline_days ? 'plant-days with no generation' : 'every plant reported generation'}>
              <Delta now={k.offline_days} before={k.prev.offline_days} invert />
              <Spark data={data.series} dataKey="offline_days" color={k.offline_days ? BAD : GOOD} />
            </Kpi>
            <Kpi icon={<Building2 size={15} />} tint="#A78BFA" title="Coverage" value={`${k.clients} client${k.clients === 1 ? '' : 's'}`}
              sub={`${k.plants} plant${k.plants === 1 ? '' : 's'}${k.best_bucket ? ` · best ${fmtBucket(k.best_bucket.bucket, gran)} (${kwh(k.best_bucket.yield_kwh)})` : ''}`} />
          </div>

          {/* ───── spotlight first: who is carrying the day, per plant and client ───── */}
          <Spotlight clients={data.by_client} plants={data.by_plant} colorOf={colorOf} />

          {/* ───── generation: all plants in one graph ───── */}
          <div style={{ ...card, marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
              <div style={{ ...sectionTitle, marginBottom: 0, flex: 1 }}>
                Generation by plant — {gran === 'day' ? 'daily' : gran === 'week' ? 'weekly' : 'monthly'}
              </div>
              {multiPlant ? (
                <>
                  <button style={pill(mode === 'side')} onClick={() => setLayout('side')}>Side by side</button>
                  <button style={pill(mode === 'stack')} onClick={() => setLayout('stack')}>Stacked</button>
                </>
              ) : null}
            </div>
            <div style={{ height: 360 }}>
              <ResponsiveContainer>
                <ComposedChart data={plantRows} margin={{ top: 10, right: 12, left: 0, bottom: 0 }}>
                  <defs>
                    {plantNames.map((n, i) => (
                      <linearGradient key={n} id={`pg-${i}`} x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={plantColor[n]} stopOpacity={1} />
                        <stop offset="100%" stopColor={plantColor[n]} stopOpacity={0.55} />
                      </linearGradient>
                    ))}
                  </defs>
                  <CartesianGrid stroke="var(--nd-w05)" vertical={false} />
                  <XAxis dataKey="bucket" tickFormatter={(v) => fmtBucket(v, gran)} tick={axisTick} tickLine={false} axisLine={false} minTickGap={18} />
                  <YAxis tick={axisTick} tickLine={false} axisLine={false} width={72} tickFormatter={(v) => Number(v).toLocaleString()} />
                  <Tooltip cursor={{ fill: 'rgba(59,130,246,.08)' }} content={<ChartTip gran={gran} />} />
                  <Legend wrapperStyle={{ fontSize: 12.5, fontFamily: T.font }} />
                  {plantNames.map((n, i) => (
                    <Bar
                      key={n} dataKey={n} name={n} fill={`url(#pg-${i})`}
                      stackId={mode === 'stack' ? 's' : undefined}
                      radius={mode === 'stack' ? (i === plantNames.length - 1 ? [5, 5, 0, 0] : 0) : [4, 4, 0, 0]}
                    />
                  ))}
                  {hasExpected && (
                    <Line type="monotone" dataKey="expected_kwh" name="Expected (all)" stroke="#F59E0B" strokeWidth={2} strokeDasharray="5 4" dot={showDots ? { r: 4, fill: '#F59E0B' } : false} />
                  )}
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* ───── PR trend + client share ───── */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(340px,1fr))', gap: 16, marginBottom: 16 }}>
            <div style={card}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
                <div style={{ ...sectionTitle, marginBottom: 0, flex: 1 }}>Performance ratio by plant</div>
                {/* the target is named here, not inside the chart, where plant lines run over it and hide it */}
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11.5, fontWeight: 700, color: WARN, background: 'rgba(251,191,36,.12)', border: '1px solid rgba(251,191,36,.35)', borderRadius: 999, padding: '4px 10px' }}>
                  <svg width="22" height="6"><line x1="0" y1="3" x2="22" y2="3" stroke={WARN} strokeWidth="2" strokeDasharray="4 3" /></svg>
                  Target {PR_TARGET}%
                </span>
              </div>
              <div style={{ height: 280 }}>
                <ResponsiveContainer>
                  <LineChart data={plantRows} margin={{ top: 10, right: 12, left: 0, bottom: 0 }}>
                    <CartesianGrid stroke="var(--nd-w05)" vertical={false} />
                    <XAxis dataKey="bucket" tickFormatter={(v) => fmtBucket(v, gran)} tick={axisTick} tickLine={false} axisLine={false} minTickGap={18} />
                    <YAxis domain={[0, 100]} tick={axisTick} tickLine={false} axisLine={false} width={40} tickFormatter={(v) => `${v}%`} />
                    <Tooltip content={<ChartTip gran={gran} unit="%" />} />
                    <Legend wrapperStyle={{ fontSize: 12.5, fontFamily: T.font }} />
                    <ReferenceLine y={PR_TARGET} stroke={WARN} strokeDasharray="4 4" />
                    {plantNames.map((n) => (
                      <Line key={n} type="monotone" dataKey={`pr|${n}`} name={n} stroke={plantColor[n]} strokeWidth={2.5} dot={showDots ? { r: 4, fill: plantColor[n] } : false} connectNulls />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>

            <ShareCard data={data} clientId={clientId} colorOf={colorOf} />
          </div>

          {/* ───── PR heatmap ───── */}
          <div style={{ ...card, marginBottom: 16 }}>
            <div style={sectionTitle}>PR heatmap — plant × {gran === 'day' ? 'day' : gran === 'week' ? 'week' : 'month'}</div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ borderCollapse: 'separate', borderSpacing: 3, fontFamily: T.font }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left', fontSize: 10, color: T.muted, padding: '0 10px 4px 0' }}>Plant</th>
                    {heat.buckets.map((b) => (
                      <th key={b} style={{ fontSize: 9.5, fontWeight: 600, color: T.muted, padding: '0 0 4px', minWidth: 34, whiteSpace: 'nowrap' }}>
                        {gran === 'day' ? b.slice(8) : fmtBucket(b, gran)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {heat.rows.map((r) => (
                    <tr key={r.plant_id}>
                      <td style={{ fontSize: 12, fontWeight: 700, padding: '0 10px 0 0', whiteSpace: 'nowrap' }}>
                        {r.plant}{multiClient ? <span style={{ color: T.muted, fontWeight: 500 }}> · {r.client}</span> : null}
                      </td>
                      {heat.buckets.map((b) => {
                        const c = r.by[b];
                        const off = c && c.offline_days > 0 && c.pr == null;
                        return (
                          <td
                            key={b}
                            title={c ? `${r.plant} · ${longBucket(b, gran)}\nPR ${c.pr == null ? '—' : `${c.pr}%`}\n${kwh(c.yield_kwh)}${c.offline_days ? `\n${c.offline_days} offline day(s)` : ''}` : 'no data'}
                            style={{
                              height: 28, minWidth: 34, borderRadius: 6, textAlign: 'center', fontSize: 10, fontWeight: 700,
                              background: !c ? 'var(--nd-w03)' : off ? 'rgba(248,113,113,.28)' : c.pr == null ? 'var(--nd-w05)' : prColor(c.pr),
                              color: !c ? 'transparent' : off ? BAD : '#06210f',
                              opacity: c && c.pr != null ? 0.92 : 1,
                            }}
                          >
                            {c ? (off ? '✕' : c.pr == null ? '' : Math.round(c.pr)) : ''}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginTop: 12, fontSize: 11, color: T.muted }}>
              {[[GOOD, '85%+'], ['#86EFAC', '80–85%'], [WARN, '70–80%'], [BAD, 'under 70%'], ['rgba(248,113,113,.4)', 'offline']].map(([c, l]) => (
                <span key={l} style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}><span style={{ width: 11, height: 11, borderRadius: 3, background: c }} />{l}</span>
              ))}
            </div>
          </div>

          {/* ───── plants + attention ───── */}
          <div style={{ marginBottom: 16 }}>
            <div style={card}>
              <div style={sectionTitle}>Plant ranking</div>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 640, fontFamily: T.font }}>
                  <thead>
                    <tr>
                      {['Plant', 'Client', 'Generation', 'vs expected', 'PR', 'Offline'].map((h) => (
                        <th key={h} style={{ textAlign: 'left', fontSize: 10, letterSpacing: '0.1em', textTransform: 'uppercase', color: T.muted, padding: '8px 10px', borderBottom: `1px solid ${T.border}` }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {data.by_plant.map((p) => {
                      const maxY = data.by_plant[0].yield_kwh || 1;
                      const vs = vsExpected(p);
                      return (
                        <tr key={p.plant_id}>
                          <td style={{ padding: '10px', borderBottom: '1px solid var(--nd-w04)', fontWeight: 700, fontSize: 13 }}>{p.plant_name}</td>
                          <td style={{ padding: '10px', borderBottom: '1px solid var(--nd-w04)', fontSize: 12.5, color: T.muted }}>{p.client_name}</td>
                          <td style={{ padding: '10px', borderBottom: '1px solid var(--nd-w04)', minWidth: 190 }}>
                            <div style={{ fontFamily: T.mono, fontSize: 12.5, marginBottom: 4 }}>{kwh(p.yield_kwh)}</div>
                            <div style={{ height: 6, borderRadius: 3, background: 'var(--nd-w06)' }}>
                              <div style={{ height: 6, borderRadius: 3, width: `${Math.max((p.yield_kwh / maxY) * 100, 2)}%`, background: 'linear-gradient(90deg,#3B82F6,#22D3A5)' }} />
                            </div>
                          </td>
                          <td style={{ padding: '10px', borderBottom: '1px solid var(--nd-w04)', fontFamily: T.mono, fontSize: 12.5, color: vs == null ? T.muted : vs >= 100 ? GOOD : vs >= 90 ? T.text : WARN }}>
                            {vs == null ? '—' : `${vs.toFixed(0)}%`}
                          </td>
                          <td style={{ padding: '10px', borderBottom: '1px solid var(--nd-w04)' }}>
                            <span style={{ fontWeight: 800, fontSize: 13, color: prColor(p.pr) }}>{p.pr == null ? '—' : `${p.pr.toFixed(1)}%`}</span>
                          </td>
                          <td style={{ padding: '10px', borderBottom: '1px solid var(--nd-w04)', fontFamily: T.mono, fontSize: 12.5, color: p.offline_days ? BAD : T.muted }}>{p.offline_days}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(340px,1fr))', gap: 16, marginBottom: 16 }}>
            <div style={card}>
              <div style={sectionTitle}>PR by plant</div>
              <div style={{ height: Math.max(180, data.by_plant.length * 34 + 30) }}>
                <ResponsiveContainer>
                  <BarChart data={[...data.by_plant].filter((p) => p.pr != null).sort((a, b) => b.pr - a.pr)} layout="vertical" margin={{ top: 0, right: 24, left: 0, bottom: 0 }}>
                    <CartesianGrid stroke="var(--nd-w05)" horizontal={false} />
                    <XAxis type="number" domain={[0, 100]} tick={axisTick} tickLine={false} axisLine={false} tickFormatter={(v) => `${v}%`} />
                    <YAxis type="category" dataKey="plant_name" width={120} tick={{ ...axisTick, fill: 'var(--nd-text)' }} tickLine={false} axisLine={false} />
                    <Tooltip cursor={{ fill: 'rgba(59,130,246,.08)' }} formatter={(v) => `${Number(v).toFixed(1)}%`} contentStyle={{ background: '#0B1220', border: '1px solid rgba(255,255,255,.12)', borderRadius: 12, fontFamily: T.font, fontSize: 12 }} itemStyle={{ color: '#fff' }} labelStyle={{ color: '#9AA7BD' }} />
                    <ReferenceLine x={PR_TARGET} stroke={WARN} strokeDasharray="4 4" />
                    <Bar dataKey="pr" name="PR" radius={[0, 6, 6, 0]} barSize={16}>
                      {[...data.by_plant].filter((p) => p.pr != null).sort((a, b) => b.pr - a.pr).map((p) => <Cell key={p.plant_id} fill={prColor(p.pr)} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div style={card}>
              <div style={sectionTitle}>Needs attention</div>
              {attention.length === 0 ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: GOOD, fontWeight: 700, fontSize: 14 }}>
                  <Sun size={18} /> All plants on target, none offline.
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 330, overflowY: 'auto' }}>
                  {attention.map((a, i) => (
                    <div key={`${a.plant.plant_id}-${i}`} style={{ display: 'flex', gap: 10, alignItems: 'center', padding: '10px 12px', borderRadius: 12, background: a.tone === 'bad' ? 'rgba(248,113,113,.10)' : 'rgba(251,191,36,.10)', border: `1px solid ${a.tone === 'bad' ? 'rgba(248,113,113,.28)' : 'rgba(251,191,36,.28)'}` }}>
                      <AlertTriangle size={16} color={a.tone === 'bad' ? BAD : WARN} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 700 }}>{a.plant.plant_name} <span style={{ color: T.muted, fontWeight: 500 }}>· {a.plant.client_name}</span></div>
                        <div style={{ fontSize: 12, color: T.muted }}>{a.text}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* ───── inverters: one graph per inverter, as many as the selected plants have ───── */}
          <div style={{ marginBottom: 16 }}>
            <div style={sectionTitle}>Inverters</div>
            {invKeys.length === 0 ? (
              <div style={{ ...card, fontSize: 13, color: T.muted, lineHeight: 1.6 }}>
                No inverter-wise figures in this period. Plant-level exports (PCL, PSM, BSP and similar) carry only the plant total;
                Salitex reports show each Knox / Tesla inverter here.
              </div>
            ) : (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(420px,1fr))', gap: 16, marginBottom: 16 }}>
                  {invKeys.map((iv, i) => {
                    const total = invKeys.reduce((a, x) => a + x.total, 0) || 1;
                    const inv = data.inverters[i];
                    const entries = (data.inverter_curves || []).filter((e) => e.plant_id === inv.plant_id && e.name === inv.name);
                    return (
                      <InverterCurveCard
                        key={iv.key} inv={iv} share={(iv.total / total) * 100} entries={entries}
                        color={INV_COLORS[i % INV_COLORS.length]} axisTick={axisTick}
                      />
                    );
                  })}
                </div>
                {invRows.length > 1 && (
                  <div style={card}>
                    <div style={sectionTitle}>Generation by inverter over time</div>
                    <div style={{ height: 300 }}>
                      <ResponsiveContainer>
                        <BarChart data={invRows} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                          <CartesianGrid stroke="var(--nd-w05)" vertical={false} />
                          <XAxis dataKey="bucket" tickFormatter={(v) => fmtBucket(v, gran)} tick={axisTick} tickLine={false} axisLine={false} minTickGap={18} />
                          <YAxis tick={axisTick} tickLine={false} axisLine={false} width={72} tickFormatter={(v) => Number(v).toLocaleString()} />
                          <Tooltip cursor={{ fill: 'rgba(59,130,246,.08)' }} content={<ChartTip gran={gran} />} />
                          <Legend wrapperStyle={{ fontSize: 12.5, fontFamily: T.font }} />
                          {invKeys.map((iv, i) => (
                            <Bar key={iv.key} dataKey={iv.key} name={iv.key} fill={INV_COLORS[i % INV_COLORS.length]} radius={[4, 4, 0, 0]} />
                          ))}
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}
